import { z } from 'zod';

import { listQuerySchema } from './common.js';
import {
  invoiceKindSchema,
  invoiceStatusSchema,
  paymentKindSchema,
  paymentStatusSchema,
  refundStatusSchema,
  type InvoiceKind,
  type InvoiceStatus,
  type PaymentKind,
  type PaymentStatus,
  type RefundStatus,
} from './enums.js';
import type { PlanTier } from './plans.js';

/**
 * ÖDEME · İADE · e-BELGE — Genişleme Adım 9.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * Neden bu dosya `billing.ts`ten AYRI
 * ═══════════════════════════════════════════════════════════════════════════
 * `billing.ts` aboneliğin ve kredinin DURUMUNU anlatıyor: hangi plandayım,
 * kaç kredim var, neyi yapabilirim. Buradakiler PARA HAREKETİNİN kaydı:
 * tahsilat, iade, kesilen belge. Tüketicileri de ayrı — durumu panel, kaydı
 * fatura geçmişi ekranı ve `/admin` okuyor.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * 🔴 Bu kayıt bugüne kadar HİÇ TUTULMUYORDU
 * ═══════════════════════════════════════════════════════════════════════════
 * `paymentId` kredi yüklendikten sonra atılıyordu; "ne zaman ne kadar tahsil
 * edildi" hiçbir yerde yazmıyordu. İade bir ödeme kimliği ve bir tutar ister
 * — yani "iade ve itiraz yönetimi" bu sözleşme olmadan yazılamazdı.
 */

// ═══════════════════════════════════════════════════════════════════════════
// Fatura profilinin ödeme anındaki kopyası
// ═══════════════════════════════════════════════════════════════════════════

/**
 * `Payment.billingSnapshot` içeriği.
 *
 * 🔴 Neden kopya: profil sonradan değişebilir (şirket taşınır, unvan
 * değişir) ama kesilmiş faturanın üstündeki bilgi DEĞİŞMEZ. Canlı profile
 * bakan bir fatura ekranı, altı ay önceki faturayı bugünkü adresle
 * gösterirdi. Adım 1'de `order_lines` aynı ilkeyle başlık/SKU/fiyatı
 * satış anından kopyalıyor.
 *
 * `identityNumber` burada TAM hâliyle duruyor: belge kesilirken lazım ve
 * o an profil değişmiş olabilir. Maskeleme yanıt katmanında.
 */
export interface BillingSnapshot {
  entityType: 'INDIVIDUAL' | 'COMPANY';
  firstName: string;
  lastName: string;
  companyTitle: string | null;
  taxOffice: string | null;
  identityNumber: string;
  phone: string;
  addressLine: string;
  district: string | null;
  city: string;
  postalCode: string | null;
  countryCode: string;
  billingEmail: string | null;
}

// ═══════════════════════════════════════════════════════════════════════════
// Ödeme kaydı
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Kaydedilecek tahsilat. `PaymentRecordService.record()` girdisi.
 *
 * 🔴 `providerPaymentId` ZORUNLU ve tekilliğin kaynağı: aynı ödeme hem
 * dönüş yolundan hem webhook'tan gelebiliyor. Veritabanındaki `@unique`
 * ikinci kaydı düşürüyor — uygulama katmanında "önce ara, yoksa yaz"
 * yapsaydık iki eşzamanlı istekte iki satır doğardı.
 */
export interface RecordPaymentInput {
  workspaceId: string;
  provider: string;
  providerPaymentId: string;
  kind: PaymentKind;
  amount: number;
  currency: string;
  plan?: PlanTier | null;
  packId?: string | null;
  credits?: number | null;
  providerSubscriptionId?: string | null;
  threeDs?: boolean | null;
  billingSnapshot?: BillingSnapshot | null;
  paidAt: Date;
}

export interface PaymentRow {
  id: string;
  kind: PaymentKind;
  status: PaymentStatus;
  amount: number;
  currency: string;
  refundedAmount: number;
  plan: PlanTier | null;
  packId: string | null;
  credits: number | null;
  paidAt: string;
  /// Kesilmiş belge varsa numarası ve PDF adresi; yoksa null.
  invoiceNo: string | null;
  invoicePdfUrl: string | null;
}

export const paymentListQuerySchema = listQuerySchema.extend({
  kind: paymentKindSchema.optional(),
  status: paymentStatusSchema.optional(),
});
export type PaymentListQuery = z.infer<typeof paymentListQuerySchema>;

// ═══════════════════════════════════════════════════════════════════════════
// İade
// ═══════════════════════════════════════════════════════════════════════════

/**
 * `POST /admin/payments/:id/refund` gövdesi.
 *
 * 🔴 Uç `/admin` altında ve bilinçli: iade PARA GERİ VERİYOR. Kiracı
 * yöneticisinin kendi kendine iade alabilmesi, ürünü bedava kullanmanın
 * bir düğmeye indirgenmiş hâli olurdu. Müşterinin talebi destek bileti
 * (`SupportTicket`) olarak geliyor, kararı platform veriyor.
 *
 * `amount` boşsa TAM iade. Kısmi iadede kalan tutardan büyük olamaz ve bu
 * kontrol sunucuda: istemcinin gönderdiği tutara güvenmek, tahsil edilenden
 * fazlasını iade etmenin yolu olurdu.
 */
export const refundRequestSchema = z.object({
  amount: z.number().positive().optional(),
  reason: z.string().trim().min(3).max(500),
});
export type RefundRequestInput = z.infer<typeof refundRequestSchema>;

export interface RefundRow {
  id: string;
  paymentId: string;
  amount: number;
  currency: string;
  status: RefundStatus;
  reason: string;
  error: string | null;
  createdAt: string;
  settledAt: string | null;
}

/// `POST /admin/payments/:id/dispute` — itiraz (chargeback) işaretleme.
/// İyzico'nun itiraz API'si YOK; bildirim e-posta/panelden geliyor.
export const disputeMarkSchema = z.object({
  note: z.string().trim().min(3).max(1000),
  /// İşareti geri almak için `false`. Banka itirazı reddedebiliyor ve o
  /// durumda ödeme yeniden normal bir tahsilat.
  disputed: z.boolean().default(true),
});
export type DisputeMarkInput = z.infer<typeof disputeMarkSchema>;

// ═══════════════════════════════════════════════════════════════════════════
// e-Belge
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Entegratöre gönderilecek belge isteği.
 *
 * `lines` tek kalemli olacak (abonelik dönemi ya da kredi paketi) ama dizi:
 * bir gün kalemli fatura gerekirse sözleşme değişmesin.
 */
export interface InvoiceIssueInput {
  kind: InvoiceKind;
  /// Bizim tarafımızdaki belge kaydının kimliği — entegratöre idempotency
  /// anahtarı olarak gidiyor.
  documentId: string;
  buyer: BillingSnapshot;
  buyerEmail: string;
  currency: string;
  /// KDV dâhil toplam. Vergi ayrıştırması entegratörün işi: oranı ve
  /// muafiyet kuralını o biliyor, biz bilmiyoruz.
  total: number;
  issuedAt: Date;
  lines: InvoiceLine[];
}

export interface InvoiceLine {
  name: string;
  quantity: number;
  unitPrice: number;
  total: number;
}

/// Entegratörün cevabı.
export interface InvoiceIssueResult {
  providerRef: string | null;
  documentNo: string | null;
  documentUuid: string | null;
  pdfUrl: string | null;
}

export interface InvoiceRow {
  id: string;
  kind: InvoiceKind;
  status: InvoiceStatus;
  documentNo: string | null;
  pdfUrl: string | null;
  issuedAt: string | null;
  error: string | null;
}

export const invoiceListQuerySchema = listQuerySchema.extend({
  status: invoiceStatusSchema.optional(),
  kind: invoiceKindSchema.optional(),
});
export type InvoiceListQuery = z.infer<typeof invoiceListQuerySchema>;

/**
 * Hata kodları. `BillingErrorCode`e EKLENMEDİ ve bu bilinçli: o sözlük
 * abonelik/kredi akışının kodları ve arayüzde faturalama ekranı onları
 * mesaja çeviriyor. Buradakilerin çoğu `/admin` ekranında görünüyor.
 */
export const CommerceOpsErrorCode = {
  /// Fatura profili yok — ödeme başlatılamaz.
  BILLING_PROFILE_REQUIRED: 'BILLING_PROFILE_REQUIRED',
  /// İade edilecek ödeme bulunamadı.
  PAYMENT_NOT_FOUND: 'PAYMENT_NOT_FOUND',
  /// İstenen tutar kalan iade edilebilir tutardan büyük.
  REFUND_AMOUNT_EXCEEDS: 'REFUND_AMOUNT_EXCEEDS',
  /// Sağlayıcı iadeyi reddetti.
  REFUND_REJECTED: 'REFUND_REJECTED',
  /// e-Belge entegratörü yapılandırılmadı.
  EINVOICE_NOT_CONFIGURED: 'EINVOICE_NOT_CONFIGURED',
  /// Entegratör belgeyi kesemedi.
  EINVOICE_FAILED: 'EINVOICE_FAILED',
} as const;
export type CommerceOpsErrorCode =
  (typeof CommerceOpsErrorCode)[keyof typeof CommerceOpsErrorCode];
